import React, { useRef, useState } from 'react';
import { Autocomplete } from '@react-google-maps/api';
import { Plus, X, ChevronUp, ChevronDown, MapPin, AlertTriangle } from 'lucide-react';

const WaypointEditor = ({ waypoints = [], onChange, mapsLoaded }) => {
  const autocompleteRefs = useRef({});
  const [manualCoordsOpen, setManualCoordsOpen] = useState({});

  const update = (newList) => onChange(newList);

  const addWaypoint = () => {
    update([...waypoints, { name: '', description: '', address: '', coords: { lat: '', lng: '' } }]);
  };

  const removeWaypoint = (index) => {
    update(waypoints.filter((_, i) => i !== index));
  };

  const updateField = (index, field, value) => {
    const next = waypoints.map((wp, i) => (i === index ? { ...wp, [field]: value } : wp));
    update(next);
  };

  const updateCoord = (index, axis, value) => {
    const next = waypoints.map((wp, i) =>
      i === index
        ? { ...wp, coords: { ...wp.coords, [axis]: value } }
        : wp
    );
    update(next);
  };

  const moveWaypoint = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= waypoints.length) return;
    const next = [...waypoints];
    [next[index], next[target]] = [next[target], next[index]];
    update(next);
  };

  const handlePlaceSelected = (index) => {
    const ac = autocompleteRefs.current[index];
    if (!ac) return;

    let place;
    try {
      place = ac.getPlace();
    } catch {
      return;
    }

    if (!place) return;

    // If the user selected a result with geometry, use it
    if (place.geometry && place.geometry.location) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      const next = waypoints.map((wp, i) =>
        i === index
          ? {
              ...wp,
              address: place.formatted_address || place.name || wp.address,
              name: wp.name || place.name || '',
              coords: { lat, lng },
            }
          : wp
      );
      update(next);
      setManualCoordsOpen((prev) => ({ ...prev, [index]: false }));
      return;
    }

    // No geometry — user typed without selecting from autocomplete.
    // Accept as free-text: keep the typed address, clear coords.
    const typedAddress = place.name || waypoints[index]?.address || '';
    if (typedAddress) {
      const next = waypoints.map((wp, i) =>
        i === index
          ? { ...wp, address: typedAddress, coords: { lat: '', lng: '' } }
          : wp
      );
      update(next);
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Let onPlaceChanged handle it if autocomplete fires;
      // otherwise the onChange on the input already captured the text.
    }
  };

  const hasCoords = (wp) =>
    wp.coords && wp.coords.lat !== '' && wp.coords.lat !== 0 && wp.coords.lng !== '' && wp.coords.lng !== 0;

  const toggleManualCoords = (index) => {
    setManualCoordsOpen((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <label className="block text-sm font-medium text-gray-700">
          Waypoints
        </label>
        <button
          type="button"
          onClick={addWaypoint}
          className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 flex items-center gap-1"
        >
          <Plus className="w-3 h-3" />
          Add Stop
        </button>
      </div>

      {waypoints.length === 0 && (
        <p className="text-sm text-gray-500 italic py-4 text-center border border-dashed border-gray-300 rounded-lg">
          No waypoints yet. Add stops to define the walking route.
        </p>
      )}

      <div className="space-y-3">
        {waypoints.map((wp, index) => (
          <div
            key={index}
            className={`border rounded-lg p-4 relative ${
              wp.address && !hasCoords(wp)
                ? 'border-amber-300 bg-amber-50/50'
                : 'border-gray-200 bg-gray-50'
            }`}
          >
            {/* Header row: number, reorder buttons, delete */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold">
                  {index + 1}
                </span>
                <span className="text-sm font-medium text-gray-700">
                  Stop {index + 1}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => moveWaypoint(index, -1)}
                  disabled={index === 0}
                  className="p-1 text-gray-500 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Move up"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => moveWaypoint(index, 1)}
                  disabled={index === waypoints.length - 1}
                  className="p-1 text-gray-500 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Move down"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => removeWaypoint(index)}
                  className="p-1 text-red-500 hover:text-red-700"
                  title="Remove stop"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Address with autocomplete */}
            <div className="mb-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Address *
              </label>
              {mapsLoaded ? (
                <Autocomplete
                  onLoad={(ac) => { autocompleteRefs.current[index] = ac; }}
                  onPlaceChanged={() => handlePlaceSelected(index)}
                >
                  <input
                    type="text"
                    defaultValue={wp.address}
                    onChange={(e) => updateField(index, 'address', e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="Search for an address or type a custom location..."
                  />
                </Autocomplete>
              ) : (
                <input
                  type="text"
                  value={wp.address}
                  onChange={(e) => updateField(index, 'address', e.target.value)}
                  className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="Loading maps..."
                />
              )}
            </div>

            {/* Name + Description row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={wp.name}
                  onChange={(e) => updateField(index, 'name', e.target.value)}
                  className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="e.g. Griffith Observatory"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={wp.description || ''}
                  onChange={(e) => updateField(index, 'description', e.target.value)}
                  className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="Quick note about this stop"
                />
              </div>
            </div>

            {/* Coords display or "no coordinates" badge */}
            {hasCoords(wp) ? (
              <div className="mt-2 flex items-center gap-1 text-xs text-gray-400">
                <MapPin className="w-3 h-3" />
                {Number(wp.coords.lat).toFixed(5)}, {Number(wp.coords.lng).toFixed(5)}
              </div>
            ) : wp.address ? (
              <div className="mt-2">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                    <AlertTriangle className="w-3 h-3" />
                    No coordinates — won't plot on map
                  </span>
                  <button
                    type="button"
                    onClick={() => toggleManualCoords(index)}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    {manualCoordsOpen[index] ? 'Hide' : 'Add lat/lng manually'}
                  </button>
                </div>

                {manualCoordsOpen[index] && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-0.5">Latitude</label>
                      <input
                        type="number"
                        step="any"
                        value={wp.coords?.lat || ''}
                        onChange={(e) => updateCoord(index, 'lat', e.target.value)}
                        className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        placeholder="e.g. 34.13623"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-0.5">Longitude</label>
                      <input
                        type="number"
                        step="any"
                        value={wp.coords?.lng || ''}
                        onChange={(e) => updateCoord(index, 'lng', e.target.value)}
                        className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        placeholder="e.g. -118.29437"
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
};

export default WaypointEditor;
